import SideSheetModal from "@/components/SideSheetModal";
import { useQuery } from "@apollo/client";
import { useNavigate } from "@/router";
import PersonSideSheetContent from "./PersonSideSheetContent";
import PersonSideSheetSkeleton from "./PersonSideSheetSkeleton";
import { MaterialNames } from "@/components/material_symbol_names";
import ErrorBoundary from "@/components/ErrorBoundary";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import {
  PERSON_SIDE_SHEET_CONTACT_QUERY,
  PERSON_SIDE_SHEET_EDUCATION_QUERY,
  PERSON_SIDE_SHEET_MEMBERSHIP_QUERY,
  PERSON_SIDE_SHEET_QUERY
} from "./queries";
import { BackofficePerson } from "@/types";

// Public types for PersonSideSheet
export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick: () => void;
  testId?: string;
  iconName?: MaterialNames;
}

export type PersonSideSheetProps = {
  open: boolean;
  onClose: () => void;
  pgaId: string | null;
  title?: string;
  customActionButtons?: CustomActionButton[];
  showDefaultSections?: boolean;
  children?: React.ReactNode;
  // Optional sections - explicitly control what to show
  showPersonalDetails?: boolean;
  showEmployment?: boolean;
  showFacility?: boolean;
  showJobApplications?: boolean;
  // Alternate contact information (hidden by default, shown only if different)
  alternateEmail?: string;
  alternatePhone?: string;
  // University name for students (shows university instead of section)
  universityName?: string;
};

export default function PersonSideSheet({
  open,
  onClose,
  pgaId,
  title = "Person Details",
  customActionButtons,
  showDefaultSections = true,
  children,
  showPersonalDetails = false,
  showEmployment = false,
  showFacility = false,
  showJobApplications = false,
  alternateEmail,
  alternatePhone,
  universityName
}: PersonSideSheetProps) {
  const navigate = useNavigate();
  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });
  const canViewPersonMembership = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonMembership
  });
  const canViewPersonEducation = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonEducation
  });

  const { data, loading, error } = useQuery(PERSON_SIDE_SHEET_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !open || !pgaId
  });

  const { data: membershipData } = useQuery(PERSON_SIDE_SHEET_MEMBERSHIP_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: !open || !pgaId || !canViewPersonMembership
  });

  const { data: contactData } = useQuery(PERSON_SIDE_SHEET_CONTACT_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: !open || !pgaId || !canViewPersonContact
  });

  const { data: educationData } = useQuery(PERSON_SIDE_SHEET_EDUCATION_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: !open || !pgaId || !canViewPersonEducation
  });

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) onClose();
  }

  const person: BackofficePerson | undefined = data?.person
    ? ({
        ...data.person,
        ...(membershipData?.person ?? {}),
        ...(contactData?.person ?? {}),
        ...(educationData?.person ?? {})
      } as BackofficePerson)
    : undefined;

  // Create default "View Person" button if no custom action buttons provided
  const actionButtons =
    customActionButtons ||
    (pgaId
      ? [
          {
            text: "View Person",
            variant: "outlined" as const,
            onClick: () => window.open(`/people/${pgaId}/person/overview`, "_blank")
          }
        ]
      : undefined);

  return (
    <SideSheetModal isSidebarOpen={open} setSidebarOpen={setIsSideSheetOpen} title={title} actions={null}>
      {loading ? (
        <PersonSideSheetSkeleton />
      ) : error ? (
        <ErrorBoundary variant="inline" title="Failed to Load Person" message={error.message} className="mt-6" />
      ) : person ? (
        <PersonSideSheetContent
          person={person}
          pgaId={pgaId as string}
          customActionButtons={actionButtons}
          showDefaultSections={showDefaultSections}
          showPersonalDetails={showPersonalDetails}
          showEmployment={showEmployment}
          showFacility={showFacility}
          showJobApplications={showJobApplications}
          alternateEmail={alternateEmail}
          alternatePhone={alternatePhone}
          universityName={universityName}
          navigate={navigate}
          showContactSections={canViewPersonContact}
        >
          {children}
        </PersonSideSheetContent>
      ) : (
        <ErrorBoundary
          variant="fullPage"
          title="Person Not Found"
          message="The person you're looking for could not be found. They may not exist or you may not have permission to view their profile."
          className="mt-6"
        />
      )}
    </SideSheetModal>
  );
}
